// src/components/pages/worker/WorkerCalendar.tsx
import { useState, useMemo, useRef, useEffect } from 'react';
import {
  RefreshCcw,
  Coffee,
  Settings,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  CalendarDays,
} from 'lucide-react';
import Joyride from 'react-joyride';
import { useTranslation } from 'react-i18next';
import WorkerCalendarTourSteps from '@/components/tour/worker/WorkerCalendarTourSteps';

import FullCalendar from '@fullcalendar/react';
import { DateSelectArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import esLocale from '@fullcalendar/core/locales/es';

import { useIntervaloCitaPorUsuario } from '@/components/hooks/useIntervaloCita';
import { useHorarioRango } from '@/components/hooks/useHorarios';
import { useDiasNoLaborablesPorUsuario } from '@/components/hooks/useDiasNoLaborables';
import { useDiasFestivos } from '@/components/hooks/useDiasFestivos';
import { useAusenciasPorUsuario } from '@/components/hooks/useAusencias';
import { useCalendarEventsPorTrabajador } from '@/components/hooks/useCalendarEvents';
import { useDescansosPorTrabajador } from '@/components/hooks/useDescansos';
import { useCitaDestacada } from '@/components/hooks/useCitaDestacada';
import { moverCita } from '@/components/hooks/useCitas';
import { useClientes } from '@/components/hooks/useClientes';
import {
  buildEventosFueraDeJornada,
  fechasDelRango,
  franjasAHorarioSemanal,
} from '@/components/utils/calendarUtils';
import { useCalendarSettings } from '@/components/hooks/useCalendarSettings';

import AppointmentDetailDialog from '@/components/pages/dialogs/AppointmentDetailDialog';
import NewAppointmentDialog from '@/components/pages/dialogs/NewAppointmentDialog';
import NewBreakDialog, { NewBreakInitialData } from '@/components/pages/dialogs/NewBreakDialog';
import BreakDetailPopover, {
  DescansoSeleccionado,
} from '@/components/pages/shared/BreakDetailPopover';
import CalendarSettingsDialog from '@/components/pages/dialogs/CalendarSettingsDialog';
import EventCard from '@/components/pages/shared/EventCard';

import type { RawAppointment } from '@/components/types/typeCalendar';

import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { toast } from 'sonner';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { getTourStyles } from '@/components/tour/tourStyles';
import { MESES } from '@/components/constants/meses';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 320, damping: 30, mass: 0.7 },
  },
};

// ── Esqueleto con la silueta de la rejilla, como en el dashboard ───────────
const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 dark:before:via-white/10 before:to-transparent';

const bone = 'rounded-md bg-gray-200/70 dark:bg-gray-700/50';

export const CalendarSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4 h-full">
    <div className={`h-14 rounded-2xl ${bone} ${shimmer}`} />

    <div className="flex items-center justify-between gap-3">
      <div className={`h-8 w-32 ${bone} ${shimmer}`} />
      <div className={`h-8 w-48 ${bone} ${shimmer}`} />
    </div>

    <div className="flex-1 min-h-0 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden">
      <div className="grid grid-cols-[56px_repeat(5,1fr)] border-b border-gray-200 dark:border-white/10">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-3">
            {i > 0 && <div className={`h-4 w-14 mx-auto ${bone} ${shimmer}`} />}
          </div>
        ))}
      </div>

      {Array.from({ length: 8 }).map((_, fila) => (
        <div
          key={fila}
          className="grid grid-cols-[56px_repeat(5,1fr)] border-b border-gray-100 dark:border-white/5"
        >
          <div className="p-2">
            <div className={`h-3 w-9 ${bone} ${shimmer}`} />
          </div>
          {Array.from({ length: 5 }).map((_, col) => (
            <div key={col} className="p-2">
              {(fila + col) % 3 === 0 && (
                <div className={`h-8 w-full ${bone} ${shimmer}`} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

// La fecha ISO local: toISOString() convierte a UTC y en España adelanta un dia.
const aISOLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

const WorkerCalendar = () => {
  const { t } = useTranslation();
  const blockEventClickRef = useRef(false);
  const idUsuario = localStorage.getItem('id_usuario') || '';
  const idEmpresa = localStorage.getItem('id_empresa') || '';

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 640 && window.innerWidth < 1024
  );
  const [isTouch, setIsTouch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [runTour, setRunTour] = useState(false);
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  type CalendarView =
    | 'timeGridDay'
    | 'timeGridWeek'
    | 'dayGridMonth'
    | 'timeGridTwoDay'
    | 'timeGridLaboral';

  const [calendarView, setCalendarView] = useState<CalendarView>(
    isMobile ? 'timeGridDay' : isTablet ? 'timeGridTwoDay' : 'timeGridLaboral'
  );

  const [customTitle, setCustomTitle] = useState('');
  const calendarRef = useRef<FullCalendar | null>(null);

  const views = [
    'timeGridDay',
    'timeGridTwoDay',
    'timeGridLaboral',
    'timeGridWeek',
    'dayGridMonth',
  ] as const;

  const [dialogAjustes, setDialogAjustes] = useState(false);
  const {
    settings: userSettings,
    setSettings,
    loading: loadingSettings,
    saveSettings,
    fetchSettings,
  } = useCalendarSettings(idUsuario);

  const [fechaInicio, setFechaInicio] = useState<string | null>(null);
  const [fechaFin, setFechaFin] = useState<string | null>(null);
  // Rango logico de la vista. No se recorta al ocultar dias, asi que
  // alimentar el horario con el evita el bucle hiddenDays -> datesSet.
  const [horarioDesde, setHorarioDesde] = useState<string | null>(null);
  const [horarioHasta, setHorarioHasta] = useState<string | null>(null);

  const [verDialogoCita, setVerDialogoCita] = useState(false);
  const [citaSeleccionada, setCitaSeleccionada] =
    useState<RawAppointment | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [breakInitialData, setBreakInitialData] = useState<NewBreakInitialData>({
    fecha: '',
    hora: '',
    horaFin: '',
  });
  const [descansoSeleccionado, setDescansoSeleccionado] = useState<
    (DescansoSeleccionado & { x: number; y: number }) | null
  >(null);
  const [newCita, setNewCita] = useState({
    trabajador: idUsuario,
    servicio: '',
    telefono: '',
    email: '',
    observaciones: '',
    fecha: '',
    hora: '',
    horaFin: '',
    num_clientes: 1,
  });

  const [firstTimeMonthView, setFirstTimeMonthView] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  const intervaloCita = useIntervaloCitaPorUsuario(idUsuario, idEmpresa);
  const diasNoLaborables = useDiasNoLaborablesPorUsuario(idUsuario, idEmpresa);
  const diasFestivos = useDiasFestivos(idEmpresa);
  const ausencias = useAusenciasPorUsuario(idUsuario, idEmpresa);
  const { agregarCliente } = useClientes();
  // Horario vigente fecha a fecha del rango visible: la temporada se
  // resuelve por dia, asi que una semana a caballo del cambio se pinta bien.
  const { franjas: franjasHorario } = useHorarioRango(
    idEmpresa,
    idUsuario,
    horarioDesde,
    horarioHasta
  );

  const fechasVisibles = useMemo(
    () =>
      horarioDesde && horarioHasta
        ? fechasDelRango(horarioDesde, horarioHasta)
        : [],
    [horarioDesde, horarioHasta]
  );

  const franjasPorFecha = useMemo(() => {
    return franjasHorario.reduce<
      Record<string, { hora_inicio: string; hora_fin: string }[]>
    >((acc, f) => {
      (acc[f.fecha] ??= []).push({
        hora_inicio: f.hora_inicio,
        hora_fin: f.hora_fin,
      });
      return acc;
    }, {});
  }, [franjasHorario]);

  const horarioSemanal = useMemo(
    () => franjasAHorarioSemanal(franjasHorario),
    [franjasHorario]
  );

  const eventosFueraJornada = useMemo(
    () => buildEventosFueraDeJornada(fechasVisibles, franjasPorFecha),
    [fechasVisibles, franjasPorFecha]
  );


  const eventsCitas = useCalendarEventsPorTrabajador(
    idUsuario,
    idEmpresa,
    fechaInicio,
    fechaFin,
    diasNoLaborables,
    diasFestivos,
    ausencias,
    reloadKey,
    horarioSemanal
  );

  const eventsDescansos = useDescansosPorTrabajador(
    idUsuario,
    idEmpresa,
    fechaInicio,
    fechaFin,
    reloadKey
  );

  const events = useMemo(
    () => [...eventsCitas, ...eventosFueraJornada, ...eventsDescansos],
    [eventsCitas, eventosFueraJornada, eventsDescansos]
  );

  // En vista Laboral solo desaparece un dia si no hay jornada NI ninguna cita
  // real en ninguna de sus fechas: una jornada especial que quitó el lunes no
  // debe esconder una cita que ya hubiera ese lunes.
  const hiddenDaysLaboral = useMemo(() => {
    if (fechasVisibles.length === 0) return [];
    const conJornada = new Set(
      fechasVisibles
        .filter((f) => (franjasPorFecha[f]?.length ?? 0) > 0)
        .map((f) => new Date(`${f}T00:00:00`).getDay())
    );

    eventsCitas.forEach((ev) => {
      const fecha = ev.extendedProps?.raw_fecha_inicio;
      if (fecha) conJornada.add(new Date(`${fecha}T00:00:00`).getDay());
    });

    if (conJornada.size === 0) return [];
    return [0, 1, 2, 3, 4, 5, 6].filter((d) => !conJornada.has(d));
  }, [fechasVisibles, franjasPorFecha, eventsCitas]);

  const scrollTime = useMemo(() => {
    const now = new Date();
    now.setHours(now.getHours() - 1);
    const [, mm] = String(intervaloCita).split(':');
    const intervalMin = parseInt(mm, 10) || 15;
    now.setMinutes(Math.floor(now.getMinutes() / intervalMin) * intervalMin, 0, 0);
    return now.toTimeString().slice(0, 8);
  }, [intervaloCita]);

  const generateTitle = (start: Date, end: Date, viewType: string) => {
    if (viewType === 'dayGridMonth')
      return isMobile
        ? `${MESES[start.getMonth()].slice(0, 3)} ${start.getFullYear()}`
        : `${MESES[start.getMonth()]} de ${start.getFullYear()}`;

    if (viewType === 'timeGridDay')
      return isMobile
        ? `${String(start.getDate()).padStart(2, '0')} ${MESES[
            start.getMonth()
          ].slice(0, 3)}`
        : `${String(start.getDate()).padStart(2, '0')} de ${
            MESES[start.getMonth()]
          } de ${start.getFullYear()}`;

    const sd = String(start.getDate()).padStart(2, '0');
    const edDate = new Date(end.getTime() - 1);
    const ed = String(edDate.getDate()).padStart(2, '0');

    return isMobile
      ? `${sd}–${ed} ${MESES[edDate.getMonth()].slice(0, 3)}`
      : `${sd}–${ed} de ${MESES[edDate.getMonth()]} de ${edDate.getFullYear()}`;
  };

  const handleSelect = (info: DateSelectArg) => {
    if (info.view.type === 'dayGridMonth') return;
    const [fecha, hora] = info.startStr.split('T');
    setNewCita((prev) => ({ ...prev, fecha, hora: hora.slice(0, 5) }));
    setDialogOpen(true);
  };

  const abrirNuevoDescanso = () => {
    const hoy = new Date().toISOString().slice(0, 10);
    setBreakInitialData({ fecha: hoy, hora: '', horaFin: '' });
    setBreakDialogOpen(true);
  };

  const handleEventDrop = async (info: any) => {
    const { event } = info;
    // Defensa extra: los descansos ya llevan editable:false, pero si algún
    // día cambia esa configuración, que no intente moverse como una cita.
    if (event.extendedProps?.tipo === 'descanso') {
      info.revert();
      return;
    }
    const fecha = event.startStr.split('T')[0];
    const hora = event.startStr.split('T')[1].slice(0, 5);
    const horaFin = event.endStr
      ? event.endStr.split('T')[1].slice(0, 5)
      : hora;
    const result = await moverCita(
      event.extendedProps.id_cita,
      fecha,
      hora,
      horaFin,
      'trabajador'
    );
    if (!result.ok) {
      toast.error(t('calendar.errorMove'));
      info.revert();
      return;
    }
    toast.success(t('calendar.successMove'));
    setReloadKey((k) => k + 1);
  };

  const formatTime = (timeStr: string): string =>
    new Date(timeStr).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });


  // El horario no entra aqui: su rango lo fija datesSet de FullCalendar, que
  // no llega a montarse mientras se muestre el esqueleto.
  const cargandoDatos =
    diasNoLaborables === undefined ||
    diasFestivos === undefined ||
    ausencias === undefined;

  usePageHelp(() => {
    setFiltrosAbiertos(true);
    setRunTour(true);
  }, !loading && !cargandoDatos);
  useTourVisibility(runTour);
  
  useEffect(() => setLoading(false), []);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const resize = () => calendarRef.current?.getApi().updateSize();
    window.addEventListener('sidebar:toggle', resize);
    return () => window.removeEventListener('sidebar:toggle', resize);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      window.dispatchEvent(
        new CustomEvent('sidebar:toggle', { detail: { collapsed: isFull } })
      );
      setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFCResize = () => calendarRef.current?.getApi().updateSize();
    window.addEventListener('fc-resize', handleFCResize);
    return () => window.removeEventListener('fc-resize', handleFCResize);
  }, []);

  // FullCalendar mide el contenedor al montarse. Al entrar por navegacion
  // el layout aun se esta asentando y la rejilla queda con el alto
  // equivocado, asi que se vuelve a medir cuando el contenedor cambia.
  //
  // updateSize() cambia el tamano del contenedor y vuelve a disparar el
  // observer, asi que solo se remide cuando el tamano cambia de verdad y
  // como mucho una vez por frame.
  useEffect(() => {
    if (cargandoDatos) return;

    const el = gridRef.current;
    if (!el) return;

    let raf = 0;
    let ancho = 0;
    let alto = 0;

    const remedir = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        calendarRef.current?.getApi().updateSize()
      );
    };

    const observador = new ResizeObserver(([entrada]) => {
      const { width, height } = entrada.contentRect;
      if (Math.abs(width - ancho) < 1 && Math.abs(height - alto) < 1) return;
      ancho = width;
      alto = height;
      remedir();
    });

    observador.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      observador.disconnect();
    };
  }, [cargandoDatos, calendarView]);

  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  useEffect(() => {
    if (isTablet) setCalendarView('timeGridTwoDay');
    else if (isMobile) setCalendarView('timeGridDay');
    else setCalendarView('timeGridLaboral');
  }, [isMobile, isTablet]);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api && scrollTime) api.scrollToTime(scrollTime);
  }, [scrollTime]);

  // Cita señalada desde una notificación (?cita=...&fecha=...)
  const { fechaInicial } = useCitaDestacada({
    calendarRef,
    events,
    calendarView,
    setCalendarView,
    forzarVistaDia: isMobile,
  });

  const viewLabels: Record<string, string> = {
    timeGridDay: t('calendar.viewDay'),
    timeGridTwoDay: t('calendar.viewTwoDay'),
    timeGridLaboral: t('calendar.viewLaboral'),
    timeGridWeek: t('calendar.viewWeek'),
    dayGridMonth: t('calendar.viewMonth'),
  };

  const legendItems = [
    { label: t('calendar.legendConfirmed'), bg: 'bg-blue-500' },
    { label: t('calendar.legendCancelled'), bg: 'bg-red-500' },
    { label: t('calendar.legendCompleted'), bg: 'bg-green-500' },
    { label: t('calendar.legendVoided'), bg: 'bg-yellow-500' },
  ];

  if (loading || cargandoDatos) return <CalendarSkeleton />;

  return (
    <motion.div
      className={`flex flex-col ${
        isMobile
          ? 'h-[calc(100dvh-5.5rem+10px-env(safe-area-inset-bottom))]'
          : 'h-[calc(100dvh-4.5rem)]'
      } lg:h-screen space-y-3 overflow-hidden`}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header: mismo text-2xl que el resto de páginas, en móvil y escritorio. */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 tour-title">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('calendar.title')}
          </h1>
        </div>
      </motion.div>

      {/* Tour */}
      <motion.div variants={itemVariants}>
        <Joyride
          steps={WorkerCalendarTourSteps}
          run={runTour}
          continuous
          showSkipButton
          spotlightClicks
          locale={{
            back: t('profiles.tour.back'),
            close: t('profiles.tour.close'),
            last: t('profiles.tour.last'),
            next: t('profiles.tour.next'),
            skip: t('profiles.tour.skip'),
          }}
          callback={({ status }) => {
            if (status === 'finished' || status === 'skipped') {
              setRunTour(false);
              setFiltrosAbiertos(false);
            }
          }}
          styles={getTourStyles(isMobile)}
        />
      </motion.div>

      {/* Filtros / Vistas */}
      <Card className={`rounded-2xl ${GLASS_CARD} overflow-hidden transition-all duration-300`}>
        <CardContent
          className={`px-4 transition-all duration-300 ${
            filtrosAbiertos ? 'py-4' : 'py-3'
          }`}
        >
          <div
            className="tour-filters-toggle flex items-center justify-between h-8 cursor-pointer"
            onClick={() => setFiltrosAbiertos((o) => !o)}
          >
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('calendar.filters')}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFiltrosAbiertos((o) => !o);
              }}
              className="p-1 rounded-lg hover:bg-white/50 dark:hover:bg-white/10 transition active:scale-90"
            >
              {filtrosAbiertos ? (
                <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>

          <div
            className={`transition-all duration-300 ${
              filtrosAbiertos
                ? 'mt-3 opacity-100 max-h-[500px]'
                : 'opacity-0 max-h-0 overflow-hidden'
            }`}
          >
            <div className="w-full tour-view-selector">
              {!isMobile && (
                <div className="flex items-center justify-end gap-2 overflow-x-auto whitespace-nowrap pb-1">
                  {views.map((v) => (
                    <button
                      key={v}
                      onClick={() => setCalendarView(v)}
                      className={`h-9 px-3.5 text-sm rounded-xl border transition-all duration-200 active:scale-[0.97] flex-shrink-0 ${
                        calendarView === v
                          ? 'bg-[#103254] border-[#103254] text-white shadow-[0_2px_10px_-2px_rgba(16,50,84,0.5),inset_0_1px_0_rgba(255,255,255,0.14)]'
                          : 'bg-white/60 dark:bg-white/5 border-gray-300/80 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-white/10'
                      }`}
                    >
                      {viewLabels[v]}
                    </button>
                  ))}
                </div>
              )}
              {isMobile && (
                <div className="flex gap-2 w-full">
                  {['timeGridDay', 'timeGridTwoDay'].map((v) => (
                    <button
                      key={v}
                      onClick={() => setCalendarView(v as any)}
                      className={`h-9 w-1/2 text-sm rounded-xl border transition-all duration-200 active:scale-[0.97] ${
                        calendarView === v
                          ? 'bg-[#103254] border-[#103254] text-white shadow-[0_2px_10px_-2px_rgba(16,50,84,0.5),inset_0_1px_0_rgba(255,255,255,0.14)]'
                          : 'bg-white/60 dark:bg-white/5 border-gray-300/80 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-white/10'
                      }`}
                    >
                      {viewLabels[v]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendario */}
      <motion.div
        variants={itemVariants}
        className={`flex-1 min-h-0 min-w-0 flex flex-col rounded-2xl ${GLASS_CARD} overflow-hidden`}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/40 dark:border-white/10 bg-white/40 dark:bg-black/30 supports-[backdrop-filter]:backdrop-blur-sm">
          <div className="flex gap-2 tour-prev-today-next">
            <button
              onClick={() => calendarRef.current?.getApi().prev()}
              className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
              aria-label={t('calendar.prev')}
            >
              <ChevronLeft className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            </button>
            <button
              onClick={() => calendarRef.current?.getApi().today()}
              className="h-8 px-3 flex items-center gap-1 rounded-lg hover:bg-white/60 dark:hover:bg-white/10 text-sm text-gray-700 dark:text-gray-300 transition active:scale-95"
            >
              <CalendarDays className="h-4 w-4" />
              <span>{t('calendar.today')}</span>
            </button>
            <button
              onClick={() => calendarRef.current?.getApi().next()}
              className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
              aria-label={t('calendar.next')}
            >
              <ChevronRight className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            </button>
          </div>

          {!isMobile && (
            <div className="font-semibold text-gray-700 dark:text-gray-200 truncate tour-title-display">
              {customTitle}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
              title={t('calendar.refresh')}
            >
              <RefreshCcw className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={abrirNuevoDescanso}
              className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
              title="Crear descanso"
            >
              <Coffee className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </button>
            {!isMobile && (
              <button
                onClick={() => {
                  if (!document.fullscreenElement)
                    document.documentElement.requestFullscreen();
                  else document.exitFullscreen();
                }}
                className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
                title={t('calendar.fullscreen')}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            )}
            <button
              onClick={() => setDialogAjustes(true)}
              className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition active:scale-90"
              title={t('calendar.settings')}
            >
              <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Legend */}
        {!isMobile && (
          <div className="flex items-center gap-4 px-4 py-2 border-b border-white/40 dark:border-white/10">
            {legendItems.map(({ label, bg }) => (
              <div key={label} className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-sm ${bg}`} />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  {label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Grid */}
        <div
          ref={gridRef}
          className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-[#1c1c1e] tour-event-grid tour-new-appointment"
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
            initialView={
              calendarView === 'timeGridLaboral' ? 'timeGridWeek' : calendarView
            }
            key={calendarView}
            initialDate={fechaInicial}
            editable={true}
            eventDrop={handleEventDrop}
            scrollTimeReset={false}
            locale={esLocale}
            selectable
            longPressDelay={200}
            selectLongPressDelay={200}
            hiddenDays={
              calendarView === 'timeGridLaboral' ? hiddenDaysLaboral : []
            }
            views={{
              timeGridTwoDay: {
                type: 'timeGrid',
                duration: { days: 2 },
                buttonText: t('calendar.viewTwoDay'),
              },
            }}
            select={handleSelect}
            slotDuration={intervaloCita}
            events={events}
            scrollTime={scrollTime}
            allDaySlot={false}
            slotMinTime="00:00:00"
            slotMaxTime="24:00:00"
            nowIndicator
            headerToolbar={false}
            dayMaxEvents
            slotEventOverlap={false}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }}
            slotLabelContent={(arg) => {
              const h = arg.date.getHours();
              const m = arg.date.getMinutes();
              return (
                <span
                  className={`fc-hour-label ${
                    m === 0 ? 'fc-hour-full' : 'fc-hour-half'
                  }`}
                >
                  {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}
                </span>
              );
            }}
            dayHeaderContent={(args) => {
              const dayNum = String(args.date.getDate()).padStart(2, '0');
              const weekDay = args.date.toLocaleDateString('es-ES', {
                weekday: 'long',
              });
              return (
                <div className="flex flex-col items-center">
                  <span className="text-[#103254] dark:text-blue-400 font-semibold">
                    {dayNum}
                  </span>
                  <span className="text-xs capitalize text-[#103254] dark:text-blue-400">
                    {weekDay}
                  </span>
                </div>
              );
            }}
            height="100%"
            selectAllow={() => true}
            dateClick={({ dateStr }) => {
              if (!isTouch) return;
              const [fecha, hora] = dateStr.split('T');
              setNewCita((p) => ({
                ...p,
                fecha,
                hora: (hora || '00:00').slice(0, 5),
              }));
              setDialogOpen(true);
            }}
            eventClick={({ event, jsEvent }) => {
              if (blockEventClickRef.current) {
                blockEventClickRef.current = false;
                return;
              }
              if (
                event.display === 'background' ||
                event.extendedProps.tipo === 'bloqueado'
              )
                return;
              if (event.extendedProps.tipo === 'descanso') {
                setDescansoSeleccionado({
                  id_descanso: event.extendedProps.id_descanso!,
                  fecha: event.extendedProps.raw_fecha_inicio!,
                  hora_inicio: event.extendedProps.raw_hora_inicio!,
                  hora_fin: event.extendedProps.raw_hora_fin!,
                  motivo: event.extendedProps.motivo_descanso ?? null,
                  x: jsEvent.clientX,
                  y: jsEvent.clientY,
                });
                return;
              }
              const fecha_cita = event.extendedProps.raw_fecha_inicio!;
              const fecha_fin = event.extendedProps.raw_fecha_fin!;
              const isoInicio = `${event.extendedProps
                .raw_fecha_inicio!}T${event.extendedProps.raw_hora_inicio!}`;
              const horaInicio = formatTime(isoInicio);
              const horaFin = event.extendedProps.raw_hora_fin!;
              setCitaSeleccionada({
                id_cita: event.extendedProps.id_cita!,
                id_servicio: event.extendedProps.id_servicio!,
                fecha_cita,
                fecha_fin,
                hora_cita: horaInicio,
                hora_fin: horaFin,
                estado_cita: event.extendedProps.estado || 'confirmado',
                tipo_pago: event.extendedProps.tipo_pago,
                observaciones: event.extendedProps.observaciones,
                nombre_cliente: event.extendedProps.nombre_cliente,
                telefono: event.extendedProps.telefono,
                email: event.extendedProps.email,
                email_contacto: event.extendedProps.email_contacto,
                motivo_cancelacion: event.extendedProps.motivo_cancelacion,
                motivo_anulado: event.extendedProps.motivo_anulado,
                fecha_cancelacion: event.extendedProps.fecha_cancelacion,
                fecha_anulado: event.extendedProps.fecha_anulado,
                fecha_completado: event.extendedProps.fecha_completado ?? null,
                fases_servicio: event.extendedProps.fases_servicio,
                nombre_servicio: event.title,
                duracion_minutos: event.extendedProps.duracion_minutos,
                nombre_completo: event.extendedProps.nombre_completo,
                precio: event.extendedProps.precio,
                num_clientes: event.extendedProps.num_clientes,
                nombre_empresa: event.extendedProps.nombre_empresa,
                tiene_valoracion: event.extendedProps.tiene_valoracion,
                puntuacion_cita: event.extendedProps.puntuacion_cita,
                comentario_valoracion:
                  event.extendedProps.comentario_valoracion ?? null,
                trabajadores_en_cita:
                  event.extendedProps.trabajadores_en_cita ?? null,
                reprogramada_cliente:
                  event.extendedProps.reprogramada_cliente ?? false,
                historial_reprogramaciones:
                  event.extendedProps.historial_reprogramaciones ?? null,
              });
              setVerDialogoCita(true);
            }}
            eventContent={({ event }) => {
              if (event.display === 'background') return null;
              if (event.extendedProps.tipo === 'descanso') {
                const motivo = event.extendedProps.motivo_descanso;
                return (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] sm:text-xs leading-tight truncate text-amber-800 dark:text-amber-200">
                    <Coffee className="w-3 h-3 shrink-0" />
                    <span className="truncate">
                      Descanso{motivo ? `: ${motivo}` : ''}
                    </span>
                  </div>
                );
              }
              return (
                <EventCard
                  event={event}
                  cfg={userSettings || {}}
                  t={t}
                  idEmpresa={idEmpresa}
                  agregarCliente={agregarCliente}
                  onReload={() => setReloadKey((k) => k + 1)}
                  calendarView={calendarView}
                  onWhatsappClick={() => {
                    blockEventClickRef.current = true;
                  }}
                />
              );
            }}
            eventDidMount={(info) => {
              // Los fondos (fuera de jornada, festivos) no son citas.
              if (info.event.display === 'background') return;
              const idCita = info.event.extendedProps?.id_cita;
              if (idCita) info.el.dataset.idCita = idCita;
              const tp = info.event.extendedProps.tipo;
              if (tp) info.el.setAttribute('data-tipo', tp);
              info.el.classList.add('tour-event-click', 'fc-event-hoverable');
              const now = new Date();
              const end = info.event.end ?? info.event.start;
              if (end && end < now) info.el.style.opacity = '0.8';
            }}
            datesSet={({ start, end, view }) => {
              let s = start,
                e = new Date(end.getTime() - 1);
              if (view.type === 'dayGridMonth' && firstTimeMonthView) {
                // Mes que muestra la vista, no el de hoy: con initialDate
                // (llegada desde una notificación) pueden no coincidir.
                const cur = view.currentStart;
                s = new Date(cur.getFullYear(), cur.getMonth(), 1);
                e = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
                setFirstTimeMonthView(false);
              } else if (view.type === 'dayGridMonth') {
                const cur = calendarRef.current?.getApi().getDate();
                if (cur) {
                  s = new Date(cur.getFullYear(), cur.getMonth(), 1);
                  e = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
                }
              }
              setCustomTitle(generateTitle(s, e, view.type));
              const fin = new Date(view.currentEnd.getTime() - 1);
              setHorarioDesde(aISOLocal(view.currentStart));
              setHorarioHasta(aISOLocal(fin));
              setFechaInicio(s.toISOString().split('T')[0]);
              setFechaFin(e.toISOString().split('T')[0]);
            }}
          />
        </div>
      </motion.div>

      {/* Dialogs */}
      <motion.div variants={itemVariants} className="tour-dialog">
        {verDialogoCita && citaSeleccionada && (
          <AppointmentDetailDialog
            cita={citaSeleccionada}
            onClose={() => setVerDialogoCita(false)}
            onCancelar={() => setVerDialogoCita(false)}
            onCompletar={() => {
              setVerDialogoCita(false);
            }}
            onReabrir={() => setVerDialogoCita(false)}
            onAnular={() => setVerDialogoCita(false)}
            incrementReload={() => setReloadKey((k) => k + 1)}
          />
        )}
        {dialogOpen && (
          <NewAppointmentDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onCitaCreada={() => {
              setDialogOpen(false);
              setReloadKey((k) => k + 1);
            }}
            incluirTrabajador={false}
            initialData={newCita}
            onChangeCita={setNewCita}
            idEmpresa={idEmpresa}
            idUsuario={idUsuario}
          />
        )}
        {breakDialogOpen && (
          <NewBreakDialog
            open={breakDialogOpen}
            onClose={() => setBreakDialogOpen(false)}
            onCreado={() => {
              setBreakDialogOpen(false);
              setReloadKey((k) => k + 1);
            }}
            idUsuario={idUsuario}
            idEmpresa={idEmpresa}
            initialData={breakInitialData}
          />
        )}
      </motion.div>

      {descansoSeleccionado && (
        <BreakDetailPopover
          x={descansoSeleccionado.x}
          y={descansoSeleccionado.y}
          descanso={descansoSeleccionado}
          onClose={() => setDescansoSeleccionado(null)}
          onEliminado={() => {
            setDescansoSeleccionado(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      <CalendarSettingsDialog
        open={dialogAjustes}
        onClose={() => {
          setDialogAjustes(false);
          fetchSettings();
        }}
        idUsuario={idUsuario}
        settings={userSettings}
        setSettings={setSettings}
        loadingSettings={loadingSettings}
        saveSettings={saveSettings}
      />
    </motion.div>
  );
};

export default WorkerCalendar;